import { InteractiveStorySchema } from '../utils/InteractiveStorySchema.js'

/**
 * AI后处理模块
 * 作为AI集成层的一部分，负责接收AI原始输出，进行解析和修复
 */
export class AIPostProcessor {
  
  /**
   * 处理AI输出
   * @param {string} aiOutput - AI原始输出
   * @param {string} genre - 故事类型
   * @param {string} style - 故事风格
   * @returns {Object} 处理结果
   */
  static async processAIOutput(aiOutput, genre = '冒险', style = '现代') {
    console.log('🔄 开始AI后处理...')
    
    const result = {
      success: false,
      data: null,
      method: '',
      errors: [],
      warnings: [],
      processingTime: 0
    }
    
    const startTime = Date.now()
    
    try {
      // 导入健壮解析器
      // 🆕 使用统一的AI输出处理器
      const { AIOutputProcessor } = await import('../utils/AIOutputProcessor.js')
      const parseResult = await AIOutputProcessor.processAIOutput(aiOutput, genre, style)
      
      // 合并警告
      result.warnings = [...result.warnings, ...parseResult.warnings]
      
      if (!parseResult.success) {
        result.errors = parseResult.errors || ['AI输出处理失败']
        console.log('❌ AI输出处理失败')
      }
      
      if (parseResult.success) {
        // 解析成功，进行数据验证和增强
        console.log('🔍 检查解析结果数据:', {
          hasData: !!parseResult.data,
          dataType: typeof parseResult.data,
          dataKeys: parseResult.data ? Object.keys(parseResult.data) : [],
          dataContent: parseResult.data // 添加完整数据内容调试
        })
        
        const enhancedData = await this.enhanceParsedData(parseResult.data, genre, style)
        
        // 标准Schema验证
        console.log('🔍 进行标准Schema验证...')
        const schemaValidation = InteractiveStorySchema.validate(enhancedData)
        
        if (!schemaValidation.isValid) {
          console.log('⚠️ Schema验证发现问题:', schemaValidation.errors)
          // 添加Schema验证错误到警告中，但不阻断流程
          const schemaWarnings = schemaValidation.errors.map(err => `Schema验证: ${err}`)
          result.warnings = [...(parseResult.warnings || []), ...schemaWarnings]
        } else {
          console.log('✅ Schema验证通过')
          result.warnings = parseResult.warnings || []
        }
        
        if (schemaValidation.warnings && schemaValidation.warnings.length > 0) {
          console.log('📋 Schema验证警告:', schemaValidation.warnings)
          const schemaWarnings = schemaValidation.warnings.map(warn => `Schema警告: ${warn}`)
          result.warnings = [...result.warnings, ...schemaWarnings]
        }
        
        // 输出数据统计
        if (schemaValidation.stats) {
          console.log('📊 故事数据统计:', schemaValidation.stats)
        }
        
        result.success = true
        result.data = enhancedData
        result.method = parseResult.method
        result.validation = schemaValidation
        
        console.log('✅ AI后处理成功')
      } else {
        // 解析失败，记录错误
        result.errors = parseResult.errors || ['解析失败']
        result.warnings = parseResult.warnings || []
        
        console.log('❌ AI后处理失败')
      }
      
    } catch (error) {
      result.errors.push(`后处理过程出错: ${error.message}`)
      console.error('❌ 后处理过程出错:', error)
    }
    
    result.processingTime = Date.now() - startTime
    console.log(`⏱️ 处理耗时: ${result.processingTime}ms`)
    
    return result
  }
  
  /**
   * 增强解析后的数据
   * @param {Object} data - 解析后的数据
   * @param {string} genre - 故事类型
   * @param {string} style - 故事风格
   * @returns {Object} 增强后的数据
   */
  static async enhanceParsedData(data, genre, style) {
    console.log('🔧 开始增强解析后的数据...')
    console.log('🔍 enhanceParsedData 接收到的原始数据:', data)
    console.log('🔍 数据类型和基本信息:', {
      isNull: data === null,
      isUndefined: data === undefined,
      dataType: typeof data,
      isObject: typeof data === 'object',
      hasKeys: data ? Object.keys(data).length > 0 : false,
      keys: data ? Object.keys(data) : []
    })
    
    // 安全检查：如果data为null或undefined，创建空对象
    if (!data || typeof data !== 'object') {
      console.log('⚠️ 传入的数据为空或无效，创建空数据结构')
      data = {}
    }
    
    console.log('📊 原始数据结构:', {
      hasOutline: !!data.outline,
      hasCharacters: Array.isArray(data.characters),
      charactersCount: data.characters?.length || 0,
      hasChapters: Array.isArray(data.chapters),
      chaptersCount: data.chapters?.length || 0,
      hasBranchPoints: Array.isArray(data.branchPoints),
      hasFinalEndings: Array.isArray(data.finalEndings)
    })
    
    const enhanced = {
      ...data,
      genre: genre,
      style: style,
      metadata: {
        createdAt: new Date().toISOString(),
        version: '1.0',
        processedBy: 'AIPostProcessor'
      }
    }
    
    // 智能修复title和summary字段 - 优先使用真实AI数据
    console.log('🔍 检查原始数据结构:')
    console.log('  data.outline:', data.outline)
    console.log('  data.characters:', data.characters)
    console.log('  data.chapters:', data.chapters)
    
    // 从多个来源尝试获取真实的标题
    let realTitle = null
    if (data.outline && data.outline.title && data.outline.title !== '精彩的科幻故事' && data.outline.title !== '精彩的奇幻故事') {
      realTitle = data.outline.title
      console.log('🔧 从outline找到真实title:', realTitle)
    } else if (data.title && data.title !== '精彩的科幻故事' && data.title !== '精彩的奇幻故事') {
      realTitle = data.title
      console.log('🔧 从data.title找到真实title:', realTitle)
    }
    
    if (realTitle) {
      enhanced.title = realTitle
      console.log('✅ 设置真实标题:', enhanced.title)
    }
    
    // 从多个来源尝试获取真实的摘要
    let realSummary = null
    if (data.outline && data.outline.summary && data.outline.summary !== '这是一个关于科幻的精彩故事' && data.outline.summary !== '这是一个关于奇幻的精彩故事') {
      realSummary = data.outline.summary
      console.log('🔧 从outline找到真实summary:', realSummary.substring(0, 50) + '...')
    } else if (data.summary && data.summary !== '这是一个关于科幻的精彩故事' && data.summary !== '这是一个关于奇幻的精彩故事') {
      realSummary = data.summary
      console.log('🔧 从data.summary找到真实summary:', realSummary.substring(0, 50) + '...')
    }
    
    if (realSummary) {
      enhanced.summary = realSummary
      console.log('✅ 设置真实摘要')
    }
    
    // 确保根级字段也被正确设置
    if (!enhanced.title && data.outline?.title) {
      enhanced.title = data.outline.title
      console.log('🔧 设置根级title:', enhanced.title)
    }
    
    if (!enhanced.summary && data.outline?.summary) {
      enhanced.summary = data.outline.summary
      console.log('🔧 设置根级summary:', enhanced.summary)
    }
    
    // 检查并修复数据结构问题
    console.log('🔍 检查原始数据结构:')
    console.log('  data.outline:', data.outline)
    console.log('  data.characters:', data.characters)
    console.log('  data.chapters:', data.chapters)
    
    // 优先使用真实数据，避免被默认数据覆盖
    if (data.outline && data.outline.title && data.outline.title !== '精彩的科幻故事') {
      enhanced.outline = data.outline
      console.log('✅ 使用原始的真实outline数据:', enhanced.outline.title)
    } else if (!enhanced.outline || enhanced.outline.title === '精彩的科幻故事') {
      console.log('⚠️ 没有真实outline数据，创建默认outline')
      enhanced.outline = this.createDefaultOutline(genre, style)
    } else {
      console.log('✅ outline存在:', enhanced.outline.title)
    }
    
    // 优先使用真实的characters数据
    if (data.characters && Array.isArray(data.characters) && data.characters.length > 0) {
      enhanced.characters = data.characters
      console.log('✅ 使用原始的真实characters数据，数量:', enhanced.characters.length)
      // 输出角色详情
      enhanced.characters.forEach((char, index) => {
        console.log(`  角色${index + 1}: ${char.name} (${char.role})`)
      })
    } else if (!enhanced.characters || enhanced.characters.length === 0) {
      console.log('⚠️ 没有真实characters数据，创建默认characters')
      enhanced.characters = this.createDefaultCharacters()
    } else {
      console.log('✅ characters存在，数量:', enhanced.characters.length)
    }
    
    // 优先使用真实的chapters数据
    if (data.chapters && Array.isArray(data.chapters) && data.chapters.length > 0) {
      enhanced.chapters = data.chapters
      console.log('✅ 使用原始的真实chapters数据，数量:', enhanced.chapters.length)
      // 输出章节详情和场景统计
      let totalScenes = 0
      enhanced.chapters.forEach((chapter, index) => {
        const scenesCount = chapter.scenes?.length || 0
        totalScenes += scenesCount
        console.log(`  章节${index + 1}: ${chapter.title} (${scenesCount}个场景)`)
      })
      console.log(`  总场景数: ${totalScenes}`)
    } else if (!enhanced.chapters || enhanced.chapters.length === 0) {
      console.log('⚠️ 没有真实chapters数据，创建默认chapters')
      enhanced.chapters = this.createDefaultChapters()
    } else {
      console.log('✅ chapters存在，数量:', enhanced.chapters.length)
    }
    
    if (!enhanced.branchPoints) {
      enhanced.branchPoints = []
    }
    
    if (!enhanced.finalEndings) {
      enhanced.finalEndings = []
    }
    
    // 验证和修复数据
    enhanced.characters = this.validateAndFixCharacters(enhanced.characters)
    enhanced.chapters = this.validateAndFixChapters(enhanced.chapters)
    
          console.log('📊 增强后的数据结构:', {
        hasOutline: !!enhanced.outline,
        outlineTitle: enhanced.outline?.title,
        hasCharacters: Array.isArray(enhanced.characters),
        charactersCount: enhanced.characters?.length || 0,
        hasChapters: Array.isArray(enhanced.chapters),
        chaptersCount: enhanced.chapters?.length || 0,
        totalScenes: enhanced.chapters?.reduce((sum, ch) => sum + (ch.scenes?.length || 0), 0) || 0
      })
      
      // 详细的数据传递调试
      console.log('🔍 AI后处理详细调试:')
      console.log('enhanced.title:', enhanced.title)
      console.log('enhanced.summary:', enhanced.summary) 
      console.log('enhanced.genre:', enhanced.genre)
      console.log('enhanced.style:', enhanced.style)
      console.log('enhanced.outline完整对象:', enhanced.outline)
      console.log('enhanced.characters完整数组:', enhanced.characters)
      console.log('enhanced.chapters完整数组:', enhanced.chapters)
      
      // 检查是否有数据丢失
      if (enhanced.title === '未命名故事' || !enhanced.title) {
        console.warn('⚠️ 标题可能未正确提取，enhanced.title为:', enhanced.title)
        console.warn('⚠️ 原始数据中的标题信息:', {
          'data.title': data.title,
          'data.outline?.title': data.outline?.title
        })
      }
    
    console.log('✅ 数据增强完成')
    return enhanced
  }
  
  /**
   * 创建默认大纲
   */
  static createDefaultOutline(genre, style) {
    return {
      title: `精彩的${genre}故事`,
      summary: `这是一个关于${genre}的精彩故事`,
      mainPlot: `在${genre}的世界中，主角将面临各种挑战和选择`,
      mainConflict: '主要冲突'
    }
  }
  
  /**
   * 创建默认角色
   */
  static createDefaultCharacters() {
    return [
      {
        id: 'char-1',
        name: '主角',
        role: 'protagonist',
        description: '故事主角',
        appearance: '神秘而引人注目',
        background: '与主角命运交织的角色',
        personality: '复杂而多面',
        imagePrompt: 'character design',
        languageStyle: '普通'
      }
    ]
  }
  
  /**
   * 创建默认章节
   */
  static createDefaultChapters() {
    return [
      {
        id: 1,
        title: '第一章',
        description: '故事开始',
        status: 'completed',
        scenes: [
          {
            id: 'scene-1-1',
            title: '故事开始',
            description: '主角登场',
            content: '这是故事开始的地方，主角将开始他的冒险。',
            imagePrompt: 'story beginning',
            hasChoices: false,
            choices: [],
            dialogues: [],
            background: '故事开始的地方',
            mood: '充满希望',
            timeOfDay: '早晨',
            location: '起始地点'
          }
        ],
        endings: []
      }
    ]
  }
  
  /**
   * 验证和修复角色数据
   */
  static validateAndFixCharacters(characters) {
    return characters.map((char, index) => ({
      id: char.id || `char-${index + 1}`,
      name: char.name || `角色${index + 1}`,
      role: char.role || 'supporting',
      description: char.description || '角色描述',
      appearance: char.appearance || '外观描述',
      background: char.background || '背景故事',
      personality: char.personality || '性格特点',
      imagePrompt: char.imagePrompt || 'character design',
      languageStyle: char.languageStyle || '普通'
    }))
  }
  
  /**
   * 验证和修复章节数据
   */
  static validateAndFixChapters(chapters) {
    return chapters.map((chapter, index) => ({
      id: chapter.id || index + 1,
      title: chapter.title || `第${index + 1}章`,
      description: chapter.description || '章节描述',
      status: chapter.status || 'completed',
      scenes: this.validateAndFixScenes(chapter.scenes || []),
      endings: chapter.endings || []
    }))
  }
  
  /**
   * 验证和修复场景数据
   */
  static validateAndFixScenes(scenes) {
    return scenes.map((scene, index) => ({
      id: scene.id || `scene-${index + 1}`,
      title: scene.title || `场景${index + 1}`,
      description: scene.description || '场景描述',
      content: scene.content || '场景内容',
      imagePrompt: scene.imagePrompt || 'scene image',
      hasChoices: scene.hasChoices || false,
      choices: scene.choices || [],
      dialogues: scene.dialogues || [],
      background: scene.background || '背景描述',
      mood: scene.mood || '情绪氛围',
      timeOfDay: scene.timeOfDay || '时间',
      location: scene.location || '地点'
    }))
  }
  
  /**
   * 生成处理报告
   */
  static generateProcessingReport(result) {
    const report = {
      summary: {
        success: result.success,
        method: result.method,
        processingTime: result.processingTime,
        hasData: !!result.data
      },
      errors: result.errors,
      warnings: result.warnings,
      recommendations: []
    }
    
    // 生成建议
    if (result.errors.length > 0) {
      report.recommendations.push('建议检查AI提示词，确保输出格式正确')
      report.recommendations.push('建议使用更严格的格式约束')
    }
    
    if (result.warnings.length > 0) {
      report.recommendations.push('建议检查提取的数据完整性')
      report.recommendations.push('建议验证关键字段是否存在')
    }
    
    if (result.method === 'nlp_extraction') {
      report.recommendations.push('建议优化AI输出格式，减少NLP提取的使用')
    }
    
    return report
  }
}
